/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import _ from 'lodash';
import IsCidr from 'is-cidr';
import React from 'react';
import { decorate, observable, computed, action, runInAction, reaction } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Container, Segment, Button, Message, Table } from 'semantic-ui-react';

import Stores from '@amzn/base-ui/dist/models/Stores';
import { displayError, displaySuccess } from '@amzn/base-ui/dist/helpers/notification';
import { swallowError } from '@amzn/base-ui/dist/helpers/utils';
import ErrorBox from '@amzn/base-ui/dist/parts/helpers/ErrorBox';
import ProgressPlaceHolder from '@amzn/base-ui/dist/parts/helpers/BasicProgressPlaceholder';
import Dropdown from '@amzn/base-ui/dist/parts/helpers/fields/DropDown';
import Form from '@amzn/base-ui/dist/parts/helpers/fields/Form';
import Input from '@amzn/base-ui/dist/parts/helpers/fields/Input';
import { createCidrFormModel } from '../../../models/forms/CreateCidrFormModel';

// expected props
// - environment (via prop)
// - scEnvironmentsStore  (via injection)
// - clientInformationStore (via injection)
// - onCancel is a function to pass back
class ScEnvironmentUpdateCidrs extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.stores = new Stores([this.getEnvStore(), this.clientInformationStore, this.envsStore]);
    });
    const sideEffect = storeReady => {
      runInAction(() => {
        if (storeReady) {
          this.ingressRules = _.map(this.environment.cidr, cidr => ({
            protocol: cidr.protocol,
            fromPort: cidr.fromPort,
            toPort: cidr.toPort,
            cidrBlocks: [],
          }));
          this.form = createCidrFormModel({
            existingValues: this.ingressRules,
          });
          _.forEach(this.ingressRules, ingressRule => {
            const origRuleIndex = _.findIndex(this.environment.cidr, {
              protocol: ingressRule.protocol,
              fromPort: ingressRule.fromPort,
              toPort: ingressRule.toPort,
            });

            this.ingressRules[origRuleIndex].cidrBlocks = this.environment.cidr[origRuleIndex].cidrBlocks;
            this.form.$(`cidr[${origRuleIndex}].cidrBlocks`).set(this.environment.cidr[origRuleIndex].cidrBlocks);
          });
        }
      });
    };
    this.disposer = reaction(() => {
      return this.stores.ready;
    }, sideEffect);
    sideEffect(this.stores.ready);
    this.validateMaxCidrs = false;
  }

  componentDidMount() {
    swallowError(this.getStores().load());
  }

  componentWillUnmount() {
    if (this.disposer) {
      this.disposer();
    }

    const store = this.getEnvStore();
    if (store) {
      store.stopHeartbeat();
    }
  }

  get environment() {
    return this.props.scEnvironment;
  }

  get envsStore() {
    return this.props.scEnvironmentsStore;
  }

  get clientInformationStore() {
    return this.props.clientInformationStore;
  }

  getEnvStore() {
    const instanceId = this.environment.id;
    const store = this.envsStore;
    return store.getScEnvironmentStore(instanceId);
  }

  handleCancel = form => {
    form.reset();
    this.props.onCancel();
  };

  getStores() {
    return this.stores;
  }

  handleSubmit = async form => {
    const store = this.envsStore;
    try {
      const values = form.values();
      await store.updateScEnvironmentCidrs(this.environment.id, values);
      displaySuccess('Update Succeeded');
      this.props.onCancel();
    } catch (error) {
      displayError(error);
      this.props.onCancel();
    }
  };

  render() {
    const stores = this.getStores();
    let content = null;
    if (stores.hasError) {
      content = <ErrorBox error={stores.error} className="pt2 mb2" />;
    } else if (stores.loading) {
      content = <ProgressPlaceHolder segmentCount={1} className="mt2 mb2" />;
    } else if (stores.ready) {
      content = this.renderForm();
    } else {
      content = null;
    }

    return <Container className="mt3">{content}</Container>;
  }

  handleAddMyIp = ruleIndex => async event => {
    event.preventDefault();
    const clientInformationStore = this.clientInformationStore;
    const currentIp = clientInformationStore.ipAddress;
    runInAction(() => {
      if (!_.includes(this.ingressRules[ruleIndex].cidrBlocks, `${currentIp}/32`)) {
        this.ingressRules[ruleIndex].cidrBlocks.push(`${currentIp}/32`);
        this.form.$(`cidr[${ruleIndex}].cidrBlocks`).set(this.ingressRules[ruleIndex].cidrBlocks);
      }
    });
  };

  addMyIpToAllIndex = event => {
    event.preventDefault();
    const clientInformationStore = this.clientInformationStore;
    const currentIp = clientInformationStore.ipAddress;
    runInAction(() => {
      const fields = this.form?.$('cidr');
      if (!fields) return;
      fields.map((item, ruleIndex) => {
        if (!_.includes(this.ingressRules[ruleIndex].cidrBlocks, `${currentIp}/32`)) {
          this.ingressRules[ruleIndex].cidrBlocks.push(`${currentIp}/32`);
          this.form.$(`cidr[${ruleIndex}].cidrBlocks`).set(this.ingressRules[ruleIndex].cidrBlocks);
        }
        return null;
      });
    });
  }

  renderForm() {
    const form = this.form;
    if (!form) {
      return null;
    }
    const fields = form.$('cidr');

    const anyWideCidr = () => {
      let wideCidrFound = false;
      _.forEach(this.ingressRules, rule => {
        if (!wideCidrFound)
          wideCidrFound = _.some(rule.cidrBlocks, cidr => {
            const parts = cidr.split('/');
            const suffixBits = parts[parts.length - 1];
            return suffixBits <= 16;
          });
      });
      return wideCidrFound;
    };

    const openCidr = () => {
      let isOpen = false;
      _.forEach(this.ingressRules, rule => {
        if (!isOpen) isOpen = _.some(rule.cidrBlocks, cidr => cidr.toString().includes('0.0.0.0') || cidr.toString().split('/')[1] < 16);
      });
      return isOpen;
    };

    const onsiteCidr = () => {
      let isOnsite = false;
      _.forEach(this.ingressRules, rule => {
        if (!isOnsite) isOnsite = _.some(rule.cidrBlocks, cidr => cidr.toString().includes('131.170.'));
      });
      return isOnsite;
    };

    const anyInvalidCidr = () => {
      let invalidCidrFound = false;
      _.forEach(this.ingressRules, rule => {
        if (!invalidCidrFound) invalidCidrFound = _.some(rule.cidrBlocks, cidr => IsCidr(cidr) !== 4);
      });
      return invalidCidrFound;
    };

    const hasEmptyCidrBlocks = () => {
      return form.values().cidr.some(rule => !rule.cidrBlocks || rule.cidrBlocks.length === 0);
    };

    const validateMaxRStudioCidr = () => {
      let status = false;
      const envOutputs = this.environment.outputs;
      const metaConnection1Type = envOutputs.find(obj => obj.OutputKey === 'MetaConnection1Type');
      if (metaConnection1Type) {
        const productName = metaConnection1Type.OutputValue;
        if (productName === 'RStudioV2') {
          status = this.validateMaxCidrs;
        }
      }
      return status;
    };

    return (
      <Segment clearing className="pl3 pr3 pb3 mb3">
        {fields.value.length === 0 && (
          <Message
            className="mb4"
            icon="warning"
            header="No Ingress Rules configured"
            content="The Security Group for this dataspace does not contain any ingress rules configured in the Service Catalog product template"
          />
        )}
        <Form form={form} onCancel={this.handleCancel} onSuccess={this.handleSubmit}>
          {({ processing, onCancel }) => (
            <>
              <Table basic="very" className='edit-sg'>
                <Table.Header>
                  <Table.Cell><div className='header-text'>Protocol</div></Table.Cell>
                  <Table.Cell><div className='header-text'>Port</div></Table.Cell>
                  <Table.Cell><div className='header-text'>CIDR Whitelist</div></Table.Cell>
                  <Table.Cell><Button active color="ardc-purple" size="mini" onClick={this.addMyIpToAllIndex}>Add to All</Button></Table.Cell>
                </Table.Header>
                <Table.Body>
                  {fields.map((field, index) => this.renderItem(field, index, processing))}
                </Table.Body>
              </Table>
              {anyWideCidr() && (
                <Message
                  className="mb4"
                  icon="warning"
                  header="Caution: Large CIDR block detected"
                  content="One or more CIDR blocks entered whitelist a large range of IP addresses. Please proceed with caution, and ensure this is intended."
                />
              )}
              {openCidr() && (
                <Message
                  negative
                  className="mb4"
                  icon="warning"
                  header="Warning: Open CIDR detected"
                  content="One of your specified CIDR blocks entered is vulnerable and allows your dataspace to be reachable from too many locations. You must remove this CIDR to continue."
                />
              )}
              {anyInvalidCidr() && (
                <Message
                  negative
                  className="mb4"
                  icon="warning"
                  header="Warning: Unsupported or Invalid CIDR block detected"
                  content="One or more options entered are not valid IPv4 CIDR blocks. Please enter CIDR blocks in the format: '255.255.255.255/32'"
                />
              )}
              {onsiteCidr() && (
                <Message
                  className="mb4"
                  icon="warning"
                  header="Information: Campus CIDR block detected"
                  content="You have entered a CIDR block that is associated with the campus network. Campus IP addresses are automatically permitted to connect to your dataspace, therefore adding this CIDR is not required."
                />
              )}
              {hasEmptyCidrBlocks() && (
                <Message
                  negative
                  className="mb4"
                  icon="warning"
                  header="Warning: Empty CIDR whitelist detected"
                  content="All CIDR whitelists must contain at least one IP range. Press the 'Add My IP' button to add your current IP address to the whitelist."
                />
              )}
              {validateMaxRStudioCidr() && (
                <Message
                  negative
                  className="mb4"
                  icon="warning"
                  header="Warning: Maximum limit CIDR reached"
                  content="You can set maximum number of 4 IPv4 CIDR in this blocks"
                />
              )}
              <Button
                className="ml2"
                size="mini"
                floated="right"
                color="blue"
                icon
                disabled={processing || anyInvalidCidr() || validateMaxRStudioCidr() || openCidr() || hasEmptyCidrBlocks()}
                type="submit"
              >
                Submit
              </Button>
              <Button floated="right" size="mini" disabled={processing} onClick={onCancel}>
                Cancel
              </Button>
            </>
          )}
        </Form>
      </Segment>
    );
  }

  renderItem(field, index, processing) {
    const options = _.map(this.ingressRules[index].cidrBlocks, cidrBlock => ({
      value: cidrBlock,
      text: cidrBlock,
    }));

    const dropdownOnChange = action((_event, data) => {
      this.ingressRules[index].cidrBlocks = data.value;
    });

    const anyWideCidr = () => {
      return _.some(this.ingressRules[index].cidrBlocks, cidr => {
        const parts = cidr.split('/');
        const n = parts[parts.length - 1];
        return n <= 16;
      });
    };

    const anyInvalidCidr = () => {
      return _.some(this.ingressRules[index].cidrBlocks, cidr => IsCidr(cidr) !== 4);
    };

    const openCidr = () => {
          return _.some(this.ingressRules[index].cidrBlocks, cidr => cidr.toString().includes('0.0.0.0') || cidr.toString().split('/')[1] < 16);
    };

    const getProtocolName = (protocolNumber, protocolType) => {
      const protocolMap = {
        '22': 'SSH',
        '80': 'HTTP',
        '443': 'HTTPS',
        '3389': 'RDP',
        '8443': 'DCV'
      };
      const name = protocolMap[protocolNumber] || protocolNumber;
      return `${name} (${protocolType})`;
    };
    
    const validateMaxRStudioCidr = () => {
      this.validateMaxCidrs = false;
      const eEnvOutputs = this.environment.outputs;
      const metaConnection1Type = eEnvOutputs.find(obj => obj.OutputKey === 'MetaConnection1Type');
      if (metaConnection1Type) {
        const productName = metaConnection1Type.OutputValue;
        const fromPort = field.value.fromPort;
        if (productName === 'RStudioV2' && fromPort === 443) {
          const cidrBlocks = field.value.cidrBlocks;
          const cidrLen = cidrBlocks.length;
          if (cidrLen > 4) {
            this.validateMaxCidrs = true;
          }
        }
      }
      return this.validateMaxCidrs;
    };

    // If you're using this component for reference:
    // The dropdown cell in this table is using the semantic-ui-react properties to display warnings and errors
    // These validation mechanisms have already been implemented in the built-in Form component which are better suited for such scenarios

    return (
      <Table.Row key={index}>
        <Table.Cell>
          <Input className='' disabled showHeader={false} field={field.$('protocol')} formatValue={(value) => getProtocolName(field.$('fromPort').value, value)}/>
        </Table.Cell>
        <Table.Cell>
          <Input className='' disabled showHeader={false} field={field.$('fromPort')} />
        </Table.Cell>
        <Table.Cell warning={anyWideCidr()} error={(anyInvalidCidr(), validateMaxRStudioCidr(), openCidr())}>
          <Dropdown
            className=''
            field={field.$('cidrBlocks')}
            allowAdditions
            search
            selection
            fluid
            multiple
            clearable
            additionLabel="Add: "
            noResultsMessage="Enter CIDR value"
            options={options}
            disabled={processing}
            onChange={dropdownOnChange}
            showHeader={false}
          />
        </Table.Cell>
        <Table.Cell>
          <Button basic color="ardc-purple" size="mini" onClick={this.handleAddMyIp(index)}>
            Add My IP
          </Button>
        </Table.Cell>
      </Table.Row>
    );
  }
}

decorate(ScEnvironmentUpdateCidrs, {
  environment: computed,
  envsStore: computed,
  clientInformationStore: computed,
  ingressRules: observable,
  form: observable,
  handleSave: action,
  handleCancel: action,
});

export default inject('scEnvironmentsStore', 'clientInformationStore')(withRouter(observer(ScEnvironmentUpdateCidrs)));
